import { Component, inject, signal, computed, OnInit, OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { BrokerInfo, ConsumerControl, ConsumerIntake, ConsumerLocation, IntakeStatus, OutboxStatus, QueueCounts, TaskIntake, ParkedMessage} from '../../core/models';

// ONE ROW PER CONSUMER, not per endpoint.
//
// The API returns one row per live ENDPOINT, and endpoints come in two shapes: plain consumer queues
// (`payment-flow`) and per-location ones carrying a location suffix
// (`upsert-magento-product-event.62da2effc63253b0b50e584c`). A product feed on forty merchants is forty
// endpoints — but ONE consumer, with ONE pair of concurrency numbers. Listing all forty at the top level
// made the caps look like forty independent settings that happened to agree.
//
// So the table is consumers, and a consumer's location queues open in a MODAL. Two affordances that never
// fight: the "N locations" button opens the dialog, the pencil opens the caps editor inline beneath the
// row. What is NOT collapsed away is PAUSE — pausing one merchant's queue is a real capability (the
// reconcile loop honours a row written against the composed key) and so is seeing which merchant's queue
// is backing up. Both live in the dialog, per queue.
interface ConsumerRow {
    endpointKeys: string[];   // every queue this consumer runs, for summing rates across the group
    consumerKey: string;          // the base/consumer queue name — what caps are written against
    consumers: string[];
    endpoints: EndpointRow[];
    endpointCount: number;   // endpoints LIVE right now (1 on the in-memory transport)
    perLocation: boolean;    // the consumer is location-scoped, from the registry
    locationCount: number;   // merchants in the platform, from the merchant table
    // THE DIVISOR, from the API. The queues this consumer actually runs — merchants on an ENABLED Magento
    // instance — which is what the reconcile loop divides a shared target by when it binds. It is NOT
    // `endpointCount` (what this instance has bound this second) and NOT `locationCount` (every merchant,
    // switched-off instances included); dividing by either of those is how this page and the save banner came
    // to print two different "per queue" numbers over the same six queues.
    targetQueues: number;

    enabled: boolean;             // every endpoint running
    pausedCount: number;
    statusLabel: string;
    statusSeverity: 'success' | 'secondary' | 'warn';

    messages: number | null;      // summed across endpoints; null when NOTHING could be read
    knownCounts: number;          // how many endpoints answered — the sum is over these
    backlog: boolean;

    // Always populated: every endpoint runs with a resolved pair, so these are never blank — a consumer
    // nobody has ever touched reads "150 prefetch / 8 at once" with a `default` tag on each.
    prefetchCount: number | null;
    prefetchSource: string;
    concurrentMessageLimit: number | null;
    concurrencySource: string;
    prefetchOverride: number | null;
    concurrencyOverride: number | null;
    // What clearing each field would fall back to, shown as a hint while editing.
    prefetchDefault: number | null;
    concurrencyDefault: number | null;
    concurrencyDefaultSource: string;
    capsVary: boolean;

    // THE TARGET, AND WHAT IT MEANS. `targetValue` is the API's `target` field verbatim — the operator's
    // number, resolved once on the server as override -> configuration -> default. It is the same value the
    // row prints, the editor prefills and the panel's arithmetic quotes, and that is the whole point: those
    // three used to read 100, 600 and 0 on one screen because each took the number from a different place.
    // `targetSharedStored` is null when nobody has ever said; `targetShared` is what the platform will
    // actually do.
    targetValue: number | null;
    targetShared: boolean;
    targetSharedStored: boolean | null;

    // WHAT IS IN FORCE, folded over this consumer's bound endpoints — against concurrentMessageLimit above,
    // which is what was ASKED FOR. Null when none of its endpoints is bound, which is not the same as
    // "running at the ceiling" and must not render as it.
    intake: ConsumerIntake | null;

    updatedAt: string | null;
    updatedBy: string | null;
}

// One live endpoint, with its count resolved from the poll when the poll knows it.
interface EndpointRow extends ConsumerControl {
    liveMessages: number | null;
    liveConsumers: number | null;
}

// One merchant's row in the locations dialog, with the polled count merged in and its status reduced to
// something an operator can read at a glance.
interface LocationRow extends ConsumerLocation {
    liveErrors: number | null;
    liveMessages: number | null;
    liveConsumers: number | null;
    // This merchant's endpoint's live limit, refreshed by the same 1s poll as the counts. Null when the
    // endpoint is not bound.
    liveIntake: IntakeStatus | null;
    statusLabel: string;
    statusSeverity: 'success' | 'secondary' | 'warn' | 'danger';
}

// The editor's state: ONE number and ONE choice.
//
// It carries the BASELINE it was opened with so "left alone" can be told from "deliberately set to this
// number" no matter how many times the per-second count poll recomputes the rows underneath it. The baseline
// is now the TARGET rather than the effective per-queue number, which is the fix for the row that read
// "400 at once" above a box reading 800: the row and the box now show the same field.
//
// `prefetch` survives only as the value to send back UNCHANGED, so a legacy prefetch override the form can no
// longer display is not silently cleared by an unrelated edit. Nothing sets it from a control.
interface CapsDraft {
    consumerKey: string;
    target: number | null;
    baseTarget: number | null;
    shared: boolean;
    baseShared: boolean;
    prefetch: number | null;
}

// The MassTransit queue control panel. Consumers and the queues each runs are discovered dynamically from
// the running bus. Pausing stops the RabbitMQ consumer(s) but leaves the durable queue bound, so messages
// accumulate and drain with zero loss on resume. Order Snapshot / Order Lifecycle are linked at the API
// (disabling Snapshot disables Lifecycle) to prevent order-timeline data loss — which is why every change
// reloads the whole list rather than mutating one row optimistically.
//
// Concurrency is editable per consumer. Both numbers are fixed by the broker when an endpoint is bound, so
// a change is applied by the reconcile loop STOPPING AND RECONNECTING that consumer's endpoints — a few
// seconds of not consuming, on queues that stay bound. Only endpoints whose numbers moved cycle.
//
// Message counts refresh about once a second (see the polling section).
@Component({
    selector: 'app-consumers',
    standalone: true,
    imports: [
        FormsModule, TableModule, CardModule, TagModule, ButtonModule, DialogModule, InputNumberModule,
        ToggleSwitchModule, MessageModule, TooltipModule, DatePipe
    ],
    template: `
        <p-card>
            <ng-template pTemplate="title"><span>Consumers</span><i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Pausing a consumer keeps its messages until it resumes. Disabling Order Snapshot also disables Order Lifecycle." tooltipPosition="top"></i></ng-template>

            <!-- WHICH BROKER THIS INSTANCE IS ON. Every environment has two during the engine migration, and the task
                 definitions decide which one each service dials - so the answer to "is Magento on the same RabbitMQ
                 as us" starts with this line and the Magento admin task's RABBITMQ_HOST. Configuration only: it says
                 which one even while that broker is down. -->
            @if (broker(); as b) {
                <p class="text-sm text-surface-600 dark:text-surface-300 mb-3 flex items-center gap-2 flex-wrap">
                    <span class="font-medium">Broker</span>
                    @if (b.transport === 'InMemory') {
                        <p-tag value="in-memory transport" severity="secondary" />
                        <span class="text-surface-500">no RabbitMQ: a local run</span>
                    } @else {
                        <code class="text-xs px-1 rounded bg-surface-100 dark:bg-surface-800" [title]="b.amqpUri ?? ''">{{ b.amqpUri ?? '—' }}</code>
                        @if (b.managementUrl) {
                            <span class="text-surface-500">management</span>
                            <a class="text-xs underline" [href]="managementUi(b.managementUrl)" target="_blank" rel="noopener">{{ b.managementUrl }}</a>
                        }
                        @if (b.locationVhostPrefix) {
                            <span class="text-surface-500">vhost prefix</span>
                            <code class="text-xs px-1 rounded bg-surface-100 dark:bg-surface-800">{{ b.locationVhostPrefix }}</code>
                        }
                    }
                </p>
            }

            <!-- THE EXPLANATION LIVES IN docs/consumer-concurrency.md, NOT HERE. Six paragraphs of how the
                 target, the gate, prefetch and the provenance tags relate stood above this table. It was
                 accurate and it was a document: nobody reading a table of live queues wants to read the
                 design first, and prose on a screen drifts from the code far more quietly than prose in a
                 file that gets reviewed with it. The badges below already name their own provenance, and
                 the strip states the instance-wide number in its own words. -->

            <!-- THE INSTANCE'S OWN NUMBER, ALWAYS ON SCREEN — not only when something is wrong.

                 Every ceiling below is read against this: the same "8 at once" is generous under a budget of
                 30 and unreachable under a budget of 3. It is also the only intake number that exists when
                 nothing is bound, which is why it lives here rather than being folded into the rows. Shown
                 permanently and quietly, because a figure that appears only during an incident is a figure
                 nobody has learned to read by the time the incident arrives. -->
            @if (task(); as t) {
                <p-message [severity]="t.backingOff ? 'warn' : 'secondary'" styleClass="mb-3">
                    <span class="text-sm">
                        <!-- ONE SCALE PER LINE. The headline is instance-wide and says so in words; nothing
                             here is a per-queue number, and nothing here is a sum of per-queue numbers. -->
                        <span class="flex items-center gap-2 flex-wrap">
                            <b>This instance sustains</b>
                            <span class="tabular-nums font-medium" [pTooltip]="budgetTooltip(t)">{{ t.derived }}</span>
                            <span class="text-surface-500">messages at once across every consumer below, bound by</span>
                            <p-tag [value]="t.bindingResource ? t.bindingResource : 'database pool'"
                                   severity="info" [pTooltip]="budgetTooltip(t)" />
                            @if (t.backingOff) {
                                <p-tag value="backing off" severity="warn" [pTooltip]="budgetTooltip(t)" />
                            }
                            @if (!t.adaptiveIntakeEnabled) {
                                <p-tag value="pool backoff off" severity="secondary"
                                       pTooltip="Backoff is switched off, so this number is not lowered under database pressure." />
                            }
                        </span>
                        <span class="block mt-1 text-xs">{{ capacityTermsLine(t) }}</span>
                        <span class="block mt-1 text-xs">{{ taskHeadline(t) }}</span>
                        <!-- THE ENTIRE VISIBLE SURFACE OF THE ONLY RUNTIME ADAPTATION LEFT. When this line
                             is absent, every number on this page was decided when its queue bound and has not
                             moved since. -->
                        @if (backoffLine(t); as b) {
                            <span class="block mt-1 text-xs font-medium text-orange-600 dark:text-orange-400">{{ b }}</span>
                        }
                        <!-- THE POST-SHRINK OVERSHOOT, SPELLED OUT. The gate pays for a shrink out of
                             completions rather than out of a blocked caller, so in-flight can sit above the
                             limit for a few seconds. Rendering the pair as two bare numbers — "35 in flight,
                             budget 30" — is a flat contradiction to everyone who has not read the gate. -->
                        @if (drainingLine(t); as d) {
                            <span class="block mt-1 text-xs text-surface-500 dark:text-surface-400">{{ d }}</span>
                        }
                        <!-- Work this instance has TAKEN and declined to start. The number that separates a
                             throttle costing throughput from a small figure on a quiet queue. -->
                        @if (taskWaitingLine(t); as w) {
                            <span class="block mt-1 text-xs font-medium">{{ w }}</span>
                        }
                        <!-- SINCE START, because the line above blinks. This page polls once a second and
                             the gate empties and refills far faster, so an instance whose budget genuinely
                             binds still reads "nothing parked" on most polls. These three do not forget. -->
                        @if (taskSinceStartLine(t); as s) {
                            <span class="block mt-1 text-xs text-surface-500 dark:text-surface-400">{{ s }}</span>
                        }
                    </span>
                </p-message>
            }

            <!-- THE BACKOFF BANNER. Only present while the database pool is actually short, and it says
                 which of the two situations this is: the backoff working (this instance taking less of a
                 shared pool, every per-queue number untouched) or the instance at its floor with work parked,
                 which is the only place that second state is visible at all — threads, CPU and memory all
                 stay flat while it happens. -->
            @if (throttleBanner(); as tb) {
                <p-message [severity]="tb.severity" styleClass="mb-3">
                    <span class="text-sm">{{ tb.text }}</span>
                </p-message>
            }

            @if (capsError(); as ce) {
                <p-message severity="error" styleClass="mb-3">{{ ce }}</p-message>
            }

            <!-- THE SAVE WORKED, AND HERE IS WHAT IT WILL DO. This is where the refusal's arithmetic went:
                 the multiplication is still shown, and it now ends in what this instance will run and which
                 resource decided that, instead of a red banner telling the operator to divide the budget by
                 the merchant count themselves. Informational severity, because nothing is wrong. -->
            @if (capsExplanation(); as ce) {
                <p-message severity="info" styleClass="mb-3">{{ ce }}</p-message>
            }

            @if (outbox(); as ob) {
                @if (ob.pending > 0) {
                    <p-message severity="warn" styleClass="mb-3">
                        Outbox: {{ ob.pending }} message(s) not yet sent to the broker
                        (oldest {{ ob.oldestSentTime | date: 'short' }}). If this stays above zero, check the
                        outbox delivery service and the broker connection.
                    </p-message>
                } @else {
                    <span class="text-xs text-surface-500 dark:text-surface-400 block mb-3">
                        Outbox empty.
                    </span>
                }
            }

            <div class="flex items-center gap-2 mb-2">
                <span class="text-xs text-surface-500 dark:text-surface-400">
                    @if (countsStale()) {
                        Message counts are the last ones read — the broker is not answering right now.
                    } @else {
                        Message counts refresh every second.
                    }
                </span>
                @if (countsStale()) {
                    <p-tag value="stale" severity="warn" />
                }
            </div>

            <p-table
                [value]="rows()"
                [loading]="loading()"
                dataKey="consumerKey"
                [rowTrackBy]="trackByConsumer"
                [expandedRowKeys]="expandedKeys()"
                [paginator]="rows().length > 10"
                [rows]="10">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Consumer</th>
                        <th>Handlers</th>
                        <th>Messages</th>
                        <th>Rate</th>
                        <th>Status</th>
                        <th>Enabled</th>
                        <th style="min-width: 14rem">Concurrency</th>
                        <th>Last changed</th>
                        <th>By</th>
                    </tr>
                </ng-template>

                <ng-template pTemplate="body" let-row>
                    <!-- The owning row is highlighted while its editor is open, so the panel beneath can
                         never be read as belonging to the row above or below it. -->
                    <tr [class]="editing() === row.consumerKey ? 'bg-primary-50 dark:bg-primary-900/30' : ''">
                        <td class="font-medium">
                            {{ row.consumerKey }}
                            <!-- Opens the locations dialog. Absent entirely for a plain consumer, so
                                 nothing on this page opens into an empty list. -->
                            @if (row.perLocation) {
                                <span class="block">
                                    <p-button
                                        [label]="row.locationCount + ' locations'"
                                        icon="pi pi-sitemap" size="small" severity="secondary" [text]="true"
                                        (onClick)="openLocations(row)"
                                        pTooltip="Status per location: connected, paused, or missing — with each queue's message count" />
                                    @if (row.pausedCount > 0) {
                                        <span class="text-xs text-surface-500 dark:text-surface-400">
                                            {{ row.pausedCount }} paused
                                        </span>
                                    }
                                </span>
                            }
                        </td>

                        <td class="text-sm text-surface-500 dark:text-surface-400">
                            @for (c of row.consumers; track c) {
                                <p-tag [value]="c" severity="secondary" styleClass="mr-1 mb-1" />
                            }
                        </td>

                        <td class="tabular-nums text-center whitespace-nowrap">
                            @if (row.messages != null) {
                                {{ row.messages }}
                                <!-- Messages waiting while nothing can eat them: paused consumer, or the
                                     broker sees no attached consumer despite the queue being enabled. -->
                                @if (row.backlog) {
                                    <p-tag value="backlog" severity="warn" styleClass="ml-1" />
                                }
                                @if (row.endpointCount > 1) {
                                    <span class="block text-xs text-surface-400">
                                        across {{ row.knownCounts }} of {{ row.endpointCount }}
                                    </span>
                                }
                            } @else {
                                <!-- Unknown, NOT zero: the broker could not be asked, or the queue is not
                                     declared on its vhost yet. -->
                                <span class="text-surface-400">—</span>
                            }
                        </td>
                        <!-- DRAIN RATE over the last five seconds, summed across this consumer's endpoints.
                             Rates add because depths do, so the group number is the sum of its queues. -->
                        <td class="whitespace-nowrap">
                            <p-tag [value]="rateLabel(rateForKeys(row.endpointKeys))"
                                   [severity]="rateSeverity(rateForKeys(row.endpointKeys))"
                                   pTooltip="Messages per second over the last 5s. Positive = draining; +N = growing." />
                        </td>

                        <td><p-tag [value]="row.statusLabel" [severity]="row.statusSeverity" /></td>

                        <td>
                            <p-toggleswitch
                                [ngModel]="row.enabled"
                                [disabled]="isSaving(row.consumerKey)"
                                (onChange)="toggleConsumer(row, $event.checked)" />
                        </td>

                        <!-- EVERY NUMBER IN THIS CELL IS PER QUEUE, and each one says so. Sums across the
                             consumer's queues and this instance's own figures live in the expanded panel under
                             headings that name their scale — they used to be mixed in here as bare numbers,
                             which is how "1-30 of 1" (a per-queue range over a per-queue ceiling, printed with
                             the left side above the right) and "Running 35 of 2400 worker slots" (a sum the
                             same page called unreachable) came to sit on one line. -->
                        <td>
                            <div class="flex items-center gap-3 flex-wrap">
                                <span class="whitespace-nowrap text-sm">
                                    <span class="tabular-nums font-medium"
                                          [pTooltip]="perQueueTooltip(row)">{{ perQueueInForce(row) }}</span>
                                    <span class="text-surface-500"> at once per queue</span>
                                </span>
                                <span class="whitespace-nowrap text-sm">
                                    <span class="tabular-nums font-medium">{{ perQueuePrefetch(row) }}</span>
                                    <span class="text-surface-500"> prefetch per queue</span>
                                    <p-tag [value]="row.prefetchSource" [severity]="sourceSeverity(row.prefetchSource)"
                                        styleClass="ml-1" [pTooltip]="prefetchTooltip(row)" />
                                </span>
                                <p-button
                                    [icon]="editing() === row.consumerKey ? 'pi pi-times' : 'pi pi-pencil'"
                                    size="small" severity="secondary" [text]="true"
                                    [disabled]="isSaving(row.consumerKey)"
                                    (onClick)="editing() === row.consumerKey ? cancelEdit() : startEdit(row)"
                                    pTooltip="Set this consumer's target" />
                            </div>
                            <!-- THE TARGET, ON THE ROW, WITH ITS SCALE IN THE SENTENCE. It is the one number
                                 an operator owns, so it is never only behind the pencil — and it is the same
                                 number the editor prefills, which is the disagreement that used to put a
                                 ceiling of 400 on the row above a box reading 800. -->
                            <span class="text-xs block mt-1 text-surface-500 dark:text-surface-400">
                                {{ targetLine(row) }}
                            </span>
                            @if (row.capsVary) {
                                <span class="text-xs text-orange-500 block mt-1">
                                    One or more location queues carry their own numbers.
                                </span>
                            }
                        </td>

                        <td class="text-sm text-surface-500">{{ row.updatedAt ? (row.updatedAt | date: 'short') : '—' }}</td>
                        <td class="text-sm text-surface-500">{{ row.updatedBy ? row.updatedBy : '—' }}</td>
                    </tr>
                </ng-template>

                <!-- THE CAPS EDITOR, and nothing else. The expansion slot is dedicated to it, driven only
                     by the pencil, so it renders directly beneath its own row, full width, with the
                     consumer named in its header. It sat in the row's own cell once, where a tall panel
                     between two similarly-named consumers read as the numbers disagreeing with themselves. -->
                <!-- pTemplate name is "expandedrow" in PrimeNG 21 (Table maps that case to
                     expandedRowTemplate). The older "rowexpansion" spelling is silently ignored -
                     no error, no warning, the row simply never expands, which is exactly how this
                     editor came to open into nothing. -->
                <ng-template pTemplate="expandedrow" let-row>
                    <tr [class]="'bg-primary-50 dark:bg-primary-900/30'">
                        <td colspan="9" class="p-0">
                            <div class="p-4">
                                <div class="p-3 rounded border border-primary-200 dark:border-primary-700 bg-surface-0 dark:bg-surface-900">
                                    <div class="text-sm font-medium mb-1">Target — {{ row.consumerKey }}</div>
                                    <div class="text-xs text-surface-500 dark:text-surface-400 mb-3">
                                        @if (row.perLocation) {
                                            <!-- The queues that EXIST, which is what reconnects — not every merchant
                                                 in the table. A merchant whose Magento instance is switched off has
                                                 no queue to rebind, and counting it here is the same overcount that
                                                 had the save banner dividing by seven over six queues. -->
                                            Applies to all {{ row.targetQueues }} location queues of this consumer, which reconnect within a few seconds.
                                        } @else {
                                            The queue reconnects within a few seconds to apply it.
                                        }
                                    </div>

                                    <!-- ONE INPUT AND ONE CHOICE. The prefetch box is gone: it follows from the
                                         worker count, an operator's number could only ever LOWER it, and a box
                                         that cannot raise anything sitting beside a badge reading "derived" was
                                         two contradictions on one line. It is still shown on the row. -->
                                    <div class="flex items-end gap-4 flex-wrap">
                                        <div class="flex flex-col gap-1">
                                            <label class="text-xs text-surface-500" [attr.for]="'tg-' + row.consumerKey">Target (messages at once)</label>
                                            <!-- max is the API's own ceiling (BrokerConcurrency.MaximumTarget):
                                                 above it the buffer, 20% up, would not fit the broker's 16-bit
                                                 unacked window. The box stops where the API stops, so the form
                                                 never offers a number it will refuse. -->
                                            <p-inputNumber
                                                [inputId]="'tg-' + row.consumerKey"
                                                [ngModel]="draft().target"
                                                (ngModelChange)="setDraftTarget($event)"
                                                [min]="1"
                                                [max]="32767"
                                                [showButtons]="false"
                                                inputStyleClass="w-28" />
                                            <!-- NEVER CALLS A DERIVED VALUE A DEFAULT. The hint reads back the
                                                 provenance the API actually reported, so "derived" stays
                                                 "derived" — CapSource.Derived exists precisely so the badge does
                                                 not lie, and a hint underneath it saying "default" undid that. -->
                                            <span class="text-xs text-surface-400">{{ targetFallbackHint(row) }}</span>
                                        </div>
                                        <div class="flex flex-col gap-1">
                                            <label class="text-xs text-surface-500">This target is</label>
                                            <div class="flex items-center gap-2">
                                                <p-button [label]="'shared across ' + (row.locationCount > 1 ? 'its queues' : 'its queue')"
                                                    size="small" [outlined]="!draft().shared" (onClick)="setDraftShared(true)"
                                                    pTooltip="One pool of messages for the whole consumer, divided across its live queues by how far behind each one is." />
                                                <p-button label="for each queue"
                                                    size="small" [outlined]="draft().shared" (onClick)="setDraftShared(false)"
                                                    pTooltip="Every queue may run the full target. The instance-wide limit still applies." />
                                            </div>
                                            <span class="text-xs text-surface-400">{{ sharedHint(row) }}</span>
                                        </div>
                                        <div class="flex items-center gap-2 flex-wrap pb-5">
                                            <p-button label="Apply" size="small" [loading]="isSaving(row.consumerKey)"
                                                (onClick)="applyCaps(row)" />
                                            <p-button label="Use defaults" size="small" severity="secondary" [text]="true"
                                                [disabled]="isSaving(row.consumerKey)" (onClick)="clearCaps(row)"
                                                pTooltip="Clear this consumer's stored target; its queues go back to the configured or built-in value." />
                                            <p-button label="Cancel" size="small" severity="secondary" [text]="true"
                                                [disabled]="isSaving(row.consumerKey)" (onClick)="cancelEdit()" />
                                        </div>
                                    </div>

                                    <!-- ── SUMS AND INSTANCE FIGURES, UNDER HEADINGS THAT NAME THEIR SCALE ──
                                         Four scales used to be rendered as bare numbers on one row — per queue,
                                         per consumer, per instance, and sum-of-ceilings — with nothing saying
                                         which was which. Sum-of-ceilings is gone entirely (the system forbids
                                         reaching it); the other three are here, each behind its own heading. -->
                                    <div class="mt-4 pt-3 border-t border-surface-200 dark:border-surface-700">
                                        <div class="text-xs font-medium mb-1">{{ consumerScopeHeading(row) }}</div>
                                        <div class="text-xs text-surface-500 dark:text-surface-400">
                                            {{ consumerAskLine(row) }}
                                        </div>
                                        @if (queueWhyLine(row); as w) {
                                            <div class="text-xs mt-1 text-surface-500 dark:text-surface-400">
                                                {{ w }}
                                            </div>
                                        }
                                        <div class="text-xs text-surface-500 dark:text-surface-400 mt-1">
                                            {{ consumerInForceLine(row) }}
                                        </div>
                                        <div class="text-xs text-surface-500 dark:text-surface-400 mt-1">
                                            {{ prefetchExplainLine(row) }}
                                        </div>
                                    </div>

                                    @if (task(); as t) {
                                        <div class="mt-3 pt-3 border-t border-surface-200 dark:border-surface-700">
                                            <div class="text-xs font-medium mb-1">This instance — shared by every consumer on this page</div>
                                            <!-- THE ONE PLACE THE CEILINGS ARE RECONCILED WITH REALITY, and it is
                                                 under the instance heading because that is its scale. The queue
                                                 above really is bound at the target; this says why six of them
                                                 at 600 is not a promise of 3,600. -->
                                            <div class="text-xs text-surface-500 dark:text-surface-400">
                                                {{ gateLine(row, t) }}
                                            </div>
                                            <div class="text-xs text-surface-500 dark:text-surface-400 mt-1">
                                                {{ capacityTermsLine(t) }}
                                            </div>
                                            <div class="text-xs text-surface-500 dark:text-surface-400 mt-1">
                                                {{ taskHeadline(t) }}
                                            </div>
                                            @if (drainingLine(t); as d) {
                                                <div class="text-xs text-surface-500 dark:text-surface-400 mt-1">{{ d }}</div>
                                            }
                                            <div class="text-xs text-surface-500 dark:text-surface-400 mt-1">
                                                {{ budgetTooltip(t) }}
                                            </div>
                                        </div>
                                    }

                                </div>
                            </div>
                        </td>
                    </tr>
                </ng-template>

                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="9">{{ error() ? error() : 'No consumers found.' }}</td>
                    </tr>
                </ng-template>
            </p-table>

            @if (error() && rows().length) {
                <p-message severity="error" styleClass="mt-3">{{ error() }}</p-message>
            }
        </p-card>

        <!-- THE LOCATION QUEUES. Forty-plus rows is the normal case, so it paginates and scrolls. The
             counts inside keep refreshing while it is open: the rows come from the same computed the table
             uses, so a poll updates the numbers in place without closing the dialog or re-sorting. -->
        <p-dialog
            [header]="'Location queues — ' + locationsFor()"
            [(visible)]="locationsOpen"
            [modal]="true"
            [dismissableMask]="true"
            [style]="{ width: '62rem' }">
            @if (locationsError(); as e) {
                <p-message severity="error" styleClass="mb-3">{{ e }}</p-message>
            }
            @if (locationsNote(); as n) {
                <p-message severity="success" styleClass="mb-3">{{ n }}</p-message>
            }
            <p-table
                [value]="locationRows()"
                [loading]="locationsLoading()"
                dataKey="key"
                [rowTrackBy]="trackByLocation"
                [paginator]="locationRows().length > 10"
                [rows]="10"
                [scrollable]="true"
                scrollHeight="26rem">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Location</th>
                        <th>Merchant</th>
                        <th>Queue</th>
                        <th>Messages</th>
                        <th>Rate</th>
                        <th>Errors</th>
                        <th>Runs with<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Per queue. To change it, set the target on the consumer." tooltipPosition="top"></i></th>
                        <th>Status</th>
                        <th>Enabled<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Pauses this location only, whatever the consumer's own switch says." tooltipPosition="top"></i></th>
                        <th>Last error</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-ep>
                    <tr>
                        <td class="font-medium">{{ ep.locationId }}</td>
                        <td class="text-sm">
                            @if (ep.merchantName) {
                                {{ ep.merchantName }}
                            } @else {
                                <span class="text-surface-400">—</span>
                            }
                            @if (ep.instanceName) {
                                <span class="block text-xs text-surface-500 dark:text-surface-400">
                                    {{ ep.instanceName }}
                                </span>
                            }
                        </td>
                        <td class="font-mono text-xs text-surface-500 dark:text-surface-400">{{ ep.key }}</td>
                        <td class="tabular-nums">
                            @if (ep.liveMessages != null) {
                                {{ ep.liveMessages }}
                                @if (ep.liveMessages > 0 && (!ep.enabled || ep.liveConsumers === 0)) {
                                    <p-tag value="backlog" severity="warn" styleClass="ml-1" />
                                }
                            } @else {
                                <!-- Unknown, not zero. On the in-memory transport that is every row, which
                                     is the honest answer: there is no broker to ask. -->
                                <span class="text-surface-400">—</span>
                            }
                        </td>
                        <td class="whitespace-nowrap">
                            <p-tag [value]="rateLabel(rateForKeys([ep.key]))"
                                   [severity]="rateSeverity(rateForKeys([ep.key]))"
                                   pTooltip="Messages per second over the last 5s for this merchant's queue." />
                        </td>
                        <!-- THE DEAD END. MassTransit parks a message in {queue}_error once its retries are
                             exhausted and nothing ever reads it again, so this number is work that has
                             stopped without any other screen saying so. Quiet at zero; loud otherwise,
                             with the button that puts them back. -->
                        <td class="whitespace-nowrap">
                            @if (ep.liveErrors == null) {
                                <span class="text-surface-400">—</span>
                            } @else if (ep.liveErrors === 0) {
                                <span class="text-surface-400 tabular-nums">0</span>
                            } @else {
                                <span class="tabular-nums font-medium text-red-600 dark:text-red-400">{{ ep.liveErrors }}</span>
                                <!-- LOOK, THEN REPLAY. The count says something failed N times; it does not
                                     say whether that is one outage worth re-sending or N messages that will
                                     fail again the moment they are. Replaying the second kind just moves the
                                     pile across and back, so the reading of it belongs next to the doing. -->
                                <p-button label="Messages" size="small" [text]="true"
                                          styleClass="ml-1" [loading]="errorsLoading() === ep.key"
                                          (onClick)="viewErrors(ep)"
                                          pTooltip="Show what is parked here and why it failed. Nothing is consumed." />
                                <p-button label="Replay" size="small" severity="danger" [text]="true"
                                          [loading]="isSaving('replay:' + ep.key)"
                                          (onClick)="replayErrors(ep)"
                                          pTooltip="Move these back onto the queue. They will fail again if the cause is not fixed." />
                            }
                        </td>
                        <!-- Inherited from the consumer, in full, per endpoint - the multiplication is the
                             point, so it is shown where the merchants are listed rather than only above.
                             Both numbers are for THIS QUEUE and the words say so — a bare pair here is the
                             same defect the consumer row was fixed for, one scale down. The live number is
                             shown IN FRONT of the bound rather than replacing it, and it can never exceed it:
                             what is in force is min(this queue's bound, its share, the instance limit). -->
                        <td class="text-xs tabular-nums whitespace-nowrap">
                            <span class="text-surface-500">this queue: </span>
                            {{ ep.prefetchCount }}<span class="text-surface-500"> prefetch, </span>
                            {{ ep.concurrentMessageLimit }}<span class="text-surface-500"> at once</span>
                            @if (ep.capsPinnedHere) {
                                <p-tag value="pinned here" severity="warn" styleClass="ml-1" />
                            }
                            <!-- WHAT THIS QUEUE WAS BOUND WITH, and which term decided it. Both numbers are
                                 for THIS QUEUE and neither moves while it is bound, so there is no instance
                                 figure here to compare them against — that lives once, in the strip above. -->
                            @if (ep.liveIntake; as li) {
                                <span class="block text-surface-500 dark:text-surface-400"
                                      [pTooltip]="locationIntakeTooltip(li)">
                                    {{ locationIntakeLine(li) }}
                                </span>
                            }
                        </td>
                        <td><p-tag [value]="ep.statusLabel" [severity]="ep.statusSeverity" /></td>
                        <td>
                            <p-toggleswitch
                                [ngModel]="ep.enabled"
                                [disabled]="isSaving(ep.key) || !ep.instanceEnabled"
                                (onChange)="toggleLocation(ep, $event.checked)" />
                        </td>
                        <td class="text-xs text-surface-500">
                            <!-- REPAIR SITS BESIDE THE ERROR IT FIXES. The failure this answers is a merchant
                                 whose vhost or queues were never built — onboarding is not allowed to fail
                                 over an unreachable broker, so a merchant can be correct in the database and
                                 absent from RabbitMQ. This row is where an operator SEES that, so it is where
                                 the second attempt belongs; the alternative was waiting for a reconcile tick
                                 that hits the same problem, or restarting the service. -->
                            @if (ep.lastError) {
                                <span class="text-red-500">{{ ep.lastError }}</span>
                                <p-button label="Repair" size="small" severity="danger" [text]="true"
                                          [loading]="repairing() === ep.locationId"
                                          (onClick)="repair(ep)" />
                            } @else {
                                —
                            }
                            @if (repairResult()[ep.locationId]; as r) {
                                <span class="block mt-1" [class.text-red-500]="!!r.error"
                                      [class.text-green-600]="!r.error">{{ r.error ?? 'Provisioned.' }}</span>
                            }
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="10">No merchants are onboarded, so this consumer has no location queues.</td></tr>
                </ng-template>
            </p-table>
        </p-dialog>

        <!-- WHAT IS ACTUALLY PARKED. The reason is the column that decides whether Replay is the right
             button, so it leads; the body is a preview because these carry whole order and product
             documents and one feed on this platform has produced 119 MB payloads. -->
        <p-dialog [(visible)]="errorsOpen" [modal]="true" [style]="{ width: '60rem' }"
                  [header]="'Parked messages — ' + (errorsQueue() ?? '')">
            <p-table [value]="errorMessages()" [scrollable]="true" scrollHeight="24rem">
                <ng-template pTemplate="header">
                    <tr><th style="width:40%">Why it failed</th><th style="width:8rem">Size</th><th>Message</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-m>
                    <tr>
                        <td class="text-sm text-red-600 dark:text-red-400">{{ m.reason ?? 'No fault reason recorded' }}</td>
                        <td class="text-sm tabular-nums">{{ m.sizeBytes }} B</td>
                        <td><pre class="text-xs whitespace-pre-wrap break-all m-0">{{ m.body }}</pre></td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="3">Nothing is parked here now.</td></tr>
                </ng-template>
            </p-table>
        </p-dialog>
    `
})
export class Consumers implements OnInit, OnDestroy {
    private readonly api = inject(MiddlewareApi);
    readonly consumers = signal<ConsumerControl[]>([]);
    readonly outbox = signal<OutboxStatus | null>(null);
    /** Which broker this API instance dials. Null until read, or when the read failed (the line is simply absent). */
    readonly broker = signal<BrokerInfo | null>(null);
    readonly loading = signal(true);
    // Keys currently being saved — only the affected control is disabled while its PUT is in flight.
    // Holds consumer keys (caps, consumer-level pause) and endpoint keys (per-location pause) alike.
    readonly saving = signal<Set<string>>(new Set());
    readonly error = signal<string | null>(null);

    // Live message counts, keyed by endpoint key. A key PRESENT with a null value means the broker was
    // asked and could not say — which renders as an em dash, never a zero.
    /** The location whose repair is in flight, so only its button spins. */
    readonly errorsOpen = signal(false);
    readonly errorsQueue = signal<string | null>(null);
    readonly errorsLoading = signal<string | null>(null);
    readonly errorMessages = signal<ParkedMessage[]>([]);

    readonly repairing = signal<string | null>(null);

    /** Per-location outcome of the last repair, kept on screen rather than toasted: the useful half is the
     *  broker's own refusal, and a message that vanishes before it is read is the thing this replaced. */
    readonly repairResult = signal<Record<string, { error: string | null }>>({});

    readonly counts = signal<Record<string, number | null>>({});
    readonly countConsumers = signal<Record<string, number | null>>({});
    // Error-queue depths, refreshed by the same 1s poll as the live counts.
    readonly countErrors = signal<Record<string, number | null>>({});

    // WHAT IS IN FORCE, refreshed by the same 1s poll. Two grains, both from the API: per endpoint (keyed by
    // endpoint key, for the locations dialog) and per consumer (keyed by base queue name, for the table).
    // A key PRESENT with a null value means the endpoint is bound to no live limiter — the page shows the
    // ceiling alone in that case rather than inventing a number for a queue nobody is consuming.
    readonly countIntake = signal<Record<string, IntakeStatus | null>>({});
    readonly consumerIntake = signal<Record<string, ConsumerIntake>>({});

    // THE TASK'S OWN NUMBER — one budget, one gate, shared by every consumer on this page.
    //
    // Kept as a page-level signal rather than read off a row because it is the ONE piece of intake state that
    // is meaningful with nothing bound at all: a process with every consumer paused still has a budget, and an
    // operator about to resume one wants to know what it is about to be let into. Fed by both the list load
    // and the per-second poll, so it is correct on the first paint and stays correct as endpoints rebind.
    readonly task = signal<TaskIntake | null>(null);
    // The per-consumer fold lists only consumers that HAVE a bound endpoint, so absence is meaningful and
    // cannot be told from "the poll has not answered yet" by looking at the map alone. Once a payload
    // carrying the fold has arrived, the poll is authoritative for every consumer including the ones it
    // omits — otherwise a consumer whose endpoints have since stopped would keep showing the throttle it
    // was in when the page was loaded.
    readonly intakePolled = signal(false);

    /**
     * Messages per second per queue, averaged over the LAST FIVE SECONDS.
     *
     * Derived from the depth samples the poll already collects, not from a new broker call: five samples
     * at one second apart, rate = (oldest - newest) / elapsed. Positive means the queue is DRAINING, which
     * is what an operator watching a backlog wants to know.
     *
     * What it is not: total throughput. It measures net change, so a queue taking 300/s while producers
     * add 300/s reads as zero — flat, not idle. The label says "draining" for that reason, and the
     * refill case shows as a negative rate rather than being hidden.
     */
    readonly rates = signal<Record<string, number | null>>({});

    /** Depth samples per queue key, newest last. Trimmed to the rate window on every poll. */
    private readonly rateSamples = new Map<string, { t: number; v: number }[]>();
    private static readonly RateWindowMs = 5000;
    readonly countsStale = signal(false);

    // The locations dialog, and the caps editor. Two separate affordances that never contend: the dialog
    // is its own surface, the editor is the table's expansion slot driven only by the pencil. Opening one
    // leaves the other exactly as it was.
    readonly locationsOpen = signal(false);
    readonly locationsFor = signal<string | null>(null);
    readonly locations = signal<ConsumerLocation[]>([]);
    readonly locationsLoading = signal(false);
    readonly locationsError = signal<string | null>(null);
    // Result of the last replay, shown in the dialog. Inline, not a toast — the page has none.
    readonly locationsNote = signal<string | null>(null);
    readonly editing = signal<string | null>(null);

    readonly draft = signal<CapsDraft>(
        { consumerKey: '', target: null, baseTarget: null, shared: false, baseShared: false, prefetch: null });
    readonly capsError = signal<string | null>(null);
    // WHAT THE ACCEPTED TARGET WILL ACTUALLY DO. Not a warning — the change was applied and nothing is wrong —
    // which is why it is a separate signal from capsError and rendered one severity down. It carries the
    // arithmetic the refusal used to carry: the multiplication, what the instance will run, and which of
    // cpu or the database pool decided that. It survives the reload that follows a successful save
    // (an error does not — the save never happened).
    readonly capsExplanation = signal<string | null>(null);

    readonly expandedKeys = computed<Record<string, boolean>>(() => {
        const open = this.editing();
        return open ? { [open]: true } : {};
    });

    // Tracking by key keeps Angular REUSING each row's view when the counts poll produces a new array
    // every second. Without it a row is destroyed and rebuilt, which would tear down an open editor
    // mid-keystroke and reset the dialog's scroll and page.
    readonly trackByConsumer = (_: number, row: ConsumerRow): string => row.consumerKey;
    readonly trackByLocation = (_: number, row: LocationRow): string => row.key;

    // Endpoints folded into consumers, with the polled counts merged in. Every consumer row carries
    // resolved caps taken from its endpoints — the API resolves them for every endpoint it returns,
    // override or not, so a consumer nobody has touched still reads its defaults rather than blank.
    readonly rows = computed<ConsumerRow[]>(() => {
        const live = this.counts();
        const liveConsumers = this.countConsumers();
        const liveIntake = this.consumerIntake();
        const polled = this.intakePolled();

        const groups = new Map<string, ConsumerControl[]>();
        for (const c of this.consumers()) {
            const consumerKey = c.baseKey ? c.baseKey : c.key;
            const bucket = groups.get(consumerKey);
            if (bucket) bucket.push(c);
            else groups.set(consumerKey, [c]);
        }

        const rows: ConsumerRow[] = [];
        for (const [consumerKey, all] of groups) {
            const endpoints: EndpointRow[] = [...all]
                .sort((a, b) => a.key.localeCompare(b.key))
                .map((e) => ({
                    ...e,
                    // The poll is authoritative once it has spoken for a key — including when it says
                    // "unknown". Before that, the value the list was loaded with.
                    liveMessages: e.key in live ? live[e.key] : (e.messages == null ? null : e.messages),
                    liveConsumers: e.key in liveConsumers
                        ? liveConsumers[e.key]
                        : (e.activeConsumers == null ? null : e.activeConsumers)
                }));

            const head = endpoints[0];
            const endpointCount = endpoints.length;
            const endpointKeys = endpoints.map((e) => e.key);
            // From the API, which knows the consumer is location-scoped from the registry — NOT inferred
            // from the endpoints in hand. Inferring it is what hid the button: on the in-memory transport
            // the only live endpoint has no location at all, so the inference said "not per-location" for
            // a consumer that plainly is.
            const perLocation = head.perLocation === true;
            const locationCount = head.locationCount == null ? 0 : head.locationCount;
            // One field, stamped by the API on every route that returns a row. The `?? 1` is a pre-`targetQueues`
            // payload, not a second opinion — an undivided target reads as per queue, which is this platform's
            // default meaning rather than an invented divisor.
            const targetQueues = head.targetQueues == null ? 1 : Math.max(1, head.targetQueues);

            const pausedCount = endpoints.filter((e) => !e.enabled).length;
            const enabled = pausedCount === 0;
            const statusLabel = enabled ? 'On' : pausedCount === endpointCount ? 'Off' : 'Partial';
            const statusSeverity: 'success' | 'secondary' | 'warn' =
                enabled ? 'success' : pausedCount === endpointCount ? 'secondary' : 'warn';

            // Sum only what is actually known. If nothing is known the aggregate is unknown too — an em
            // dash — rather than a zero somebody would read as "the queue is empty".
            const known = endpoints.filter((e) => e.liveMessages != null);
            const messages = known.length === 0
                ? null
                : known.reduce((sum, e) => sum + (e.liveMessages as number), 0);
            const attached = endpoints
                .filter((e) => e.liveConsumers != null)
                .reduce((sum, e) => sum + (e.liveConsumers as number), 0);
            const backlog = messages != null && messages > 0 && (!enabled || attached === 0);

            const capsVary = endpoints.some(
                (e) => e.prefetchCount !== head.prefetchCount || e.concurrentMessageLimit !== head.concurrentMessageLimit);

            const stamped = endpoints.filter((e) => !!e.updatedAt).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
            const latest = stamped.length > 0 ? stamped[0] : null;

            // The poll is authoritative once it has spoken, exactly as for the counts — including when it
            // says nothing about this consumer, which means no endpoint of it is bound. Before its first
            // response the list's own fold stands in, so a freshly loaded page already reads correctly
            // instead of showing an un-throttled row for one second.
            const intake = polled
                ? (consumerKey in liveIntake ? liveIntake[consumerKey] : null)
                : (head.consumerIntake ? head.consumerIntake : null);

            rows.push({
                consumerKey,
                consumers: head.consumers,
                endpoints,
                endpointCount,
                endpointKeys,
                perLocation,
                locationCount,
                targetQueues,
                enabled,
                pausedCount,
                statusLabel,
                statusSeverity,
                messages,
                knownCounts: known.length,
                backlog,
                prefetchCount: head.prefetchCount == null ? null : head.prefetchCount,
                prefetchSource: head.prefetchSource ? head.prefetchSource : 'default',
                concurrentMessageLimit: head.concurrentMessageLimit == null ? null : head.concurrentMessageLimit,
                concurrencySource: head.concurrencySource ? head.concurrencySource : 'default',
                prefetchOverride: head.prefetchOverride == null ? null : head.prefetchOverride,
                concurrencyOverride: head.concurrencyOverride == null ? null : head.concurrencyOverride,
                prefetchDefault: head.prefetchDefault == null ? null : head.prefetchDefault,
                concurrencyDefault: head.concurrencyDefault == null ? null : head.concurrencyDefault,
                concurrencyDefaultSource: head.concurrencyDefaultSource ? head.concurrencyDefaultSource : 'default',
                capsVary,
                // ONE SOURCE. The API resolves the target and stamps it on every row; nothing here picks
                // between candidates, because picking between candidates is what produced three answers.
                // The `??` chain behind it is a pre-`target` payload, not a second opinion.
                targetValue: head.target != null
                    ? head.target
                    : (head.concurrencyOverride != null ? head.concurrencyOverride : head.concurrencyDefault ?? null),
                targetShared: head.targetSharedEffective === true,
                targetSharedStored: head.targetShared == null ? null : head.targetShared,
                intake,
                updatedAt: latest ? latest.updatedAt : null,
                updatedBy: latest && latest.updatedBy ? latest.updatedBy : null
            });
        }

        return rows.sort((a, b) => a.consumerKey.localeCompare(b.consumerKey));
    });

    // The dialog's rows: the authoritative merchant list fetched when it opened, with the per-second
    // counts merged in so the numbers stay live while it is open. Deriving these from the page's live
    // endpoints instead is what left the dialog empty — on the in-memory transport (and on any host whose
    // endpoints have not connected) there are no per-location endpoints to derive from.
    readonly locationRows = computed<LocationRow[]>(() => {
        const live = this.counts();
        const liveConsumers = this.countConsumers();
        const liveErrorCounts = this.countErrors();
        const liveIntake = this.countIntake();

        return this.locations().map((l) => {
            const messages = l.key in live ? live[l.key] : (l.messages == null ? null : l.messages);
            const consumers = l.key in liveConsumers
                ? liveConsumers[l.key]
                : (l.activeConsumers == null ? null : l.activeConsumers);

            // Four honestly different states, in the order an operator cares about them.
            let statusLabel: string;
            let statusSeverity: 'success' | 'secondary' | 'warn' | 'danger';
            if (!l.instanceEnabled) {
                // Not a fault: the Magento instance behind this merchant is switched off, so no endpoint
                // is meant to exist. Saying "not connected" here sends people hunting a broker problem.
                statusLabel = 'Instance off';
                statusSeverity = 'secondary';
            } else if (!l.enabled) {
                statusLabel = 'Paused';
                statusSeverity = 'secondary';
            } else if (l.connected) {
                statusLabel = 'Connected';
                statusSeverity = 'success';
            } else {
                // Should be consuming and is not — the row worth opening this dialog for.
                statusLabel = l.lastError ? 'Failed' : 'Not connected';
                statusSeverity = l.lastError ? 'danger' : 'warn';
            }

            // Inherited rows read the CONSUMER's live numbers rather than the copy fetched with the
            // dialog, so the two can never disagree even for the moment between a save and a refetch.
            // A row that pins its own keeps what the server said about it.
            const caps = this.locationsCaps();
            const prefetchCount = l.capsPinnedHere || caps.prefetch == null ? l.prefetchCount : caps.prefetch;
            const concurrentMessageLimit =
                l.capsPinnedHere || caps.concurrency == null ? l.concurrentMessageLimit : caps.concurrency;

            const errors = l.key in liveErrorCounts
                ? liveErrorCounts[l.key]
                : (l.errorMessages == null ? null : l.errorMessages);

            // The counts route reports EVERY observed endpoint, with a null intake for one that is bound to
            // no live limiter — so a key present with null is "this endpoint has no limit in force", not
            // "unknown", and the row falls back to showing the ceiling alone.
            const intake = l.key in liveIntake
                ? liveIntake[l.key]
                : (l.intake ? l.intake : null);

            return {
                ...l, prefetchCount, concurrentMessageLimit,
                liveMessages: messages, liveConsumers: consumers, liveErrors: errors,
                liveIntake: intake,
                statusLabel, statusSeverity
            };
        });
    });

    // The consumer's caps, echoed in the dialog so an operator reading a backlog can see what these queues
    // are running at without closing it.
    readonly locationsCaps = computed<{ prefetch: number | null; concurrency: number | null }>(() => {
        const key = this.locationsFor();
        const row = key ? this.rows().find((r) => r.consumerKey === key) : undefined;
        return {
            prefetch: row ? row.prefetchCount : null,
            concurrency: row ? row.concurrentMessageLimit : null
        };
    });

    // ---- polling ---------------------------------------------------------------------------------
    //
    // A 1s interval against the API's ~1s server-side cache, NOT a held-open long poll. It gives the same
    // freshness with far simpler failure behaviour: nothing to time out, nothing holding a connection per
    // viewer, and a failed tick is just a retry. The chain is re-armed only after each response resolves,
    // so ticks can never overlap or pile up behind a slow broker.
    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private pollInFlight = false;
    private pollDelayMs = 1000;
    private destroyed = false;

    ngOnInit(): void {
        this.reload();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.scheduleNextPoll(0);
    }

    ngOnDestroy(): void {
        // A tab left open overnight must not keep polling, and neither must a page the operator navigated
        // away from.
        this.destroyed = true;
        this.clearPollTimer();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    private readonly onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') {
            this.pollDelayMs = 1000; // a backed-off tab starts fresh when it is looked at again
            this.scheduleNextPoll(0);
        } else {
            this.clearPollTimer();
        }
    };

    private clearPollTimer(): void {
        if (this.pollTimer !== null) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private scheduleNextPoll(delayMs: number): void {
        this.clearPollTimer();
        if (this.destroyed || document.visibilityState !== 'visible') return;
        this.pollTimer = setTimeout(() => this.pollCounts(), delayMs);
    }

    private pollCounts(): void {
        if (this.pollInFlight) {
            this.scheduleNextPoll(this.pollDelayMs);
            return;
        }
        this.pollInFlight = true;
        this.api.getConsumerQueueCounts().subscribe({
            next: (payload) => {
                this.pollInFlight = false;
                this.applyCounts(payload);
                this.pollDelayMs = 1000;
                this.scheduleNextPoll(this.pollDelayMs);
            },
            error: () => {
                this.pollInFlight = false;
                // Back off rather than hammering a broker or an API that is already unhappy, and leave the
                // last known numbers on screen with the stale marker instead of blanking the table.
                this.countsStale.set(true);
                this.pollDelayMs = Math.min(this.pollDelayMs * 2, 30000);
                this.scheduleNextPoll(this.pollDelayMs);
            }
        });
    }

    // Immutable writes into two small maps. Only these signals change, so the open dialog, its scroll and
    // page, the editor and its draft are all untouched — the numbers update in place.
    private applyCounts(payload: QueueCounts): void {
        const messages: Record<string, number | null> = {};
        const consumers: Record<string, number | null> = {};
        const errors: Record<string, number | null> = {};
        const intake: Record<string, IntakeStatus | null> = {};
        for (const q of payload.queues) {
            messages[q.key] = q.messages == null ? null : q.messages;
            consumers[q.key] = q.activeConsumers == null ? null : q.activeConsumers;
            errors[q.key] = q.errorMessages == null ? null : q.errorMessages;
            intake[q.key] = q.intake ? q.intake : null;
        }
        this.counts.set(messages);
        this.countConsumers.set(consumers);
        this.countErrors.set(errors);
        this.countIntake.set(intake);

        // The per-consumer fold. Rebuilt whole every tick rather than merged, so a consumer that has stopped
        // being throttled — or stopped being bound at all — disappears from the map instead of lingering.
        if (Array.isArray(payload.consumers)) {
            const byConsumer: Record<string, ConsumerIntake> = {};
            for (const c of payload.consumers) byConsumer[c.consumerKey] = c;
            this.consumerIntake.set(byConsumer);
            this.intakePolled.set(true);
        }

        // The gate moves on its own under pool pressure, so it rides the poll and
        // is only ever replaced by a real payload. An older API that does not send it leaves the last known
        // value alone rather than blanking the strip that explains every other number on the page.
        if (payload.task) this.task.set(payload.task);

        this.updateRates(messages);
        // Intake is read from the API's own routers, not the broker, so it is NOT covered by this flag: the
        // limits stay true while the message counts go stale.
        this.countsStale.set(payload.stale === true);
    }

    /**
     * Moves a location's errored messages back onto its queue.
     *
     * Bounded server-side, so this is a batch rather than a promise to finish: the response says how many
     * moved and how many are left, and the operator presses again if they want another pass. Replayed
     * messages fail again if the cause has not been fixed — the result line says so rather than implying
     * the backlog is resolved.
     */
    replayErrors(location: LocationRow): void {
        const consumerKey = this.locationsFor();
        if (!consumerKey) return;

        const marker = 'replay:' + location.key;
        this.setSaving(marker, true);
        this.locationsError.set(null);

        this.api.replayConsumerErrors(consumerKey, location.locationId).subscribe({
            next: (r) => {
                this.setSaving(marker, false);
                this.locationsNote.set(
                    r.remaining != null && r.remaining > 0
                        ? `Moved ${r.moved} message(s) back onto ${location.key}; ${r.remaining} still in the error queue.`
                        : `Moved ${r.moved} message(s) back onto ${location.key}. The error queue is empty.`);
                this.loadLocations(consumerKey);
            },
            error: (e) => {
                this.setSaving(marker, false);
                this.locationsError.set(this.describe(e));
            }
        });
    }

    /**
     * Folds this tick's depths into the five-second window and republishes the rates.
     *
     * A queue whose depth is unknown this tick keeps its samples rather than being reset: a single failed
     * management call should not blank a rate that was fine a second ago. Keys the poll stops reporting
     * altogether are dropped so the map cannot grow without bound as merchants come and go.
     */
    private updateRates(messages: Record<string, number | null>): void {
        const now = Date.now();
        const cutoff = now - Consumers.RateWindowMs;
        const next: Record<string, number | null> = {};

        for (const [key, value] of Object.entries(messages)) {
            if (value == null) {
                // Unknown depth: leave the history alone, report what the window still supports.
                next[key] = this.rateFrom(this.rateSamples.get(key), cutoff);
                continue;
            }

            const samples = this.rateSamples.get(key) ?? [];
            samples.push({ t: now, v: value });
            // Keep one sample older than the cutoff so a full window is measurable the moment it exists.
            while (samples.length > 2 && samples[1].t < cutoff) samples.shift();
            this.rateSamples.set(key, samples);
            next[key] = this.rateFrom(samples, cutoff);
        }

        for (const key of [...this.rateSamples.keys()]) {
            if (!(key in messages)) this.rateSamples.delete(key);
        }

        this.rates.set(next);
    }

    /** (oldest - newest) / seconds, or null until there are two samples spanning real time. */
    private rateFrom(samples: { t: number; v: number }[] | undefined, cutoff: number): number | null {
        if (!samples || samples.length < 2) return null;

        const newest = samples[samples.length - 1];
        // The oldest sample still inside the window, else the oldest we have — a shorter window is a
        // rougher number, not a wrong one, and beats showing nothing for the first few seconds.
        const oldest = samples.find((x) => x.t >= cutoff) ?? samples[0];
        const seconds = (newest.t - oldest.t) / 1000;
        if (seconds <= 0) return null;

        return (oldest.v - newest.v) / seconds;
    }

    /** A group's rate is the sum of its endpoints' — rates add, because depths do. */
    rateForKeys(keys: string[]): number | null {
        const all = this.rates();
        const known = keys.map((k) => all[k]).filter((r): r is number => r != null);
        return known.length === 0 ? null : known.reduce((sum, r) => sum + r, 0);
    }

    /** "12.4/s" draining, "-8/s" filling, blank below a tenth of a message a second. */
    rateLabel(rate: number | null): string {
        if (rate == null) return '—';
        if (Math.abs(rate) < 0.1) return '0/s';
        const rounded = Math.abs(rate) >= 10 ? Math.round(Math.abs(rate)) : Math.round(Math.abs(rate) * 10) / 10;
        return `${rate < 0 ? '+' : ''}${rounded}/s`;
    }

    rateSeverity(rate: number | null): 'success' | 'warn' | 'secondary' {
        if (rate == null || Math.abs(rate) < 0.1) return 'secondary';
        return rate > 0 ? 'success' : 'warn';
    }

    // ---- effective vs ceiling ----------------------------------------------------------------------
    //
    // Everything below exists to keep one promise: the number the operator set is always on screen, and so
    // is the number actually running, and they are never merged. A page that shows only the setting while
    // the gate runs below it is a page that lies during exactly the incident it was built for.

    /**
     * WHAT ONE QUEUE IS RUNNING — "4" when this consumer's merchants agree, "2–6" when they do not.
     *
     * A range rather than an average, because an average matches no queue on the platform and the operator's
     * next question is always "which ones". The locations dialog answers that per merchant.
     *
     * Both ends of the range are per queue, and so is everything printed beside them. The old cell rendered
     * this range against `concurrentMessageLimit` as "{range} of {ceiling}" — a per-queue span over a
     * per-queue ceiling that had already been clamped, which is how "1-30 of 1" appeared: a left side above
     * the right, on a panel insisting the right side is never exceeded.
     */
    perQueueInForce(row: ConsumerRow): string {
        const i = row.intake;
        if (!i) return '—';
        return i.lowestConcurrency === i.highestConcurrency
            ? String(i.lowestConcurrency)
            : `${i.lowestConcurrency}–${i.highestConcurrency}`;
    }

    /** The buffer, per queue — the same spread treatment, from the same fold. */
    perQueuePrefetch(row: ConsumerRow): string {
        const i = row.intake;
        if (!i) return row.prefetchCount == null ? '—' : String(row.prefetchCount);
        return i.lowestPrefetch === i.highestPrefetch
            ? String(i.lowestPrefetch)
            : `${i.lowestPrefetch}–${i.highestPrefetch}`;
    }

    /** Why this queue is where it is, in one hover: the derivation, spelled out. */
    perQueueTooltip(row: ConsumerRow): string {
        const i = row.intake;
        if (!i) return 'No queue of this consumer is bound on this instance, so nothing is in force.';

        const head = i.endpoints === 1
            ? `One live queue, running ${this.perQueueInForce(row)} at once.`
            : `${i.endpoints} live queues, each running ${this.perQueueInForce(row)} at once.`;
        return i.why ? `${head} ${i.why}` : head;
    }

    /** Prefetch is DERIVED — the badge already says so, and this says from what. */
    prefetchTooltip(row: ConsumerRow): string {
        if (row.prefetchSource === 'derived') {
            return 'Twenty per cent above the workers, with at least one spare message buffered however small '
                + 'the target is. It moves when the target moves, which is why it is not called a default. You '
                + 'cannot raise it from here: a buffer larger than the workers can drain does not go faster, it '
                + 'CLAIMS messages that stay unacked and invisible to every other instance until this one gets '
                + 'to them.';
        }
        return 'The buffer the broker keeps this queue fed from. Normally twenty per cent above the workers; '
            + 'this one carries a stored or configured value, which can only ever lower the derivation.';
    }

    /** THE TARGET, ON THE ROW, WITH ITS SCALE IN THE SENTENCE — the same field the editor prefills. */
    targetLine(row: ConsumerRow): string {
        const target = row.targetValue;
        if (target == null) return 'No target set — this consumer runs its built-in default.';

        const live = row.intake ? row.intake.endpoints : row.endpointCount;
        const scope = row.targetShared ? 'shared across its queues' : 'per queue';
        const queues = row.perLocation && row.locationCount > live
            ? `${live} of ${row.locationCount} queues live`
            : live === 1 ? '1 live queue' : `${live} live queues`;
        return `Target ${target} ${scope} · ${queues}`;
    }

    /** What clearing the box would fall back to, NAMED BY ITS REAL PROVENANCE. The hint used to read
     *  "empty = default N" underneath a value the row itself labelled `derived`, which undid the whole point
     *  of having a separate word for a number the resources compute. */
    targetFallbackHint(row: ConsumerRow): string {
        if (row.concurrencyDefault == null) return 'empty = this consumer\'s built-in value';
        const source = row.concurrencyDefaultSource === 'derived'
            ? 'derived from this instance\'s resources'
            : row.concurrencyDefaultSource === 'configured'
                ? 'from an environment setting'
                : 'the built-in value';
        return `empty = ${row.concurrencyDefault}, ${source}`;
    }

    /** What the choice will actually do for THIS consumer, before Apply. Teaching without blocking.
     *
     *  Over `targetQueues` — the API's divisor — because this sentence predicts the arithmetic the save is
     *  about to perform, and predicting it against a different count is how a hint becomes a promise the
     *  banner underneath immediately contradicts. */
    sharedHint(row: ConsumerRow): string {
        const queues = row.targetQueues;
        const target = this.draft().target;
        if (queues <= 1) return 'One live queue, so the two settings are the same thing here.';
        if (target == null) return `${queues} live queues.`;
        return this.draft().shared
            ? `${target} at once across all ${queues} live queues.`
            : `${target} at once on each of ${queues} live queues — asks for ${target * queues}.`;
    }

    // ---- the task budget ---------------------------------------------------------------------------
    //
    // THE NUMBER EVERY OTHER NUMBER ON THIS PAGE IS READ AGAINST. A ceiling of 8 means one thing when the
    // instance may work 30 messages at once and quite another when it may work 3, and until this was on
    // screen the two were indistinguishable: the operator's setting was honoured, the queue was not moving,
    // and nothing said the process had capped itself somewhere else entirely.

    /** The strip's second line: what the instance is working right now, across how many queues. Instance
     *  scale throughout, and it never divides one scale by another. */
    taskHeadline(t: TaskIntake): string {
        const state = t.backingOff
            ? `Holding intake at ${t.limit} of that`
            : `Letting through all ${t.derived}`;
        const across = t.endpoints === 1 ? '1 bound queue' : `${t.endpoints} bound queues`;
        const inFlight = t.draining
            ? `${t.inUse} in flight`
            : `${t.inUse} of them in flight right now`;
        return `${state}, across ${across}. ${inFlight}.`;
    }

    /** THE TWO TERMS, EACH NAMED. Both are message COUNTS — the connection pool less its reserve, and the
     *  cores times the messages one core sustains — so the lower of them is the answer with nothing to
     *  convert and no third scale to reconcile. */
    capacityTermsLine(t: TaskIntake): string {
        const counts: string[] = [];
        if (t.poolBound) counts.push(`database pool ${t.poolBound}`);
        if (t.cpuBound) counts.push(`cpu ${t.cpuBound}`);

        if (counts.length === 0) return this.budgetSourceLine(t);

        return counts.length === 1
            ? `${counts[0]} is the message count this instance sustains.`
            : `${counts.join(' · ')} — the lower of the two is what this instance sustains, as one gate across `
              + `the ${t.endpoints === 1 ? '1 queue' : `${t.endpoints} queues`} it hosts.`;
    }

    /** THE POST-SHRINK OVERSHOOT, RENDERED RATHER THAN LEFT AS A CONTRADICTION. The gate pays for a shrink out
     *  of completions, never out of a blocked caller, so in-flight legitimately sits above the limit for a few
     *  seconds after a step down. Two bare numbers ("35 in flight, budget 30") read as a bug to everyone who
     *  has not read the gate's source. */
    drainingLine(t: TaskIntake): string | null {
        if (!t.draining || !t.finishingAboveLimit) return null;
        const n = t.finishingAboveLimit;
        return `${t.inUse} in flight against a gate of ${t.limit}: ${n} message${n === 1 ? '' : 's'} `
            + `${n === 1 ? 'was' : 'were'} already started when the limit came down and ${n === 1 ? 'is' : 'are'} `
            + 'being allowed to finish. Nothing new starts until in-flight is back under the limit.';
    }

    /**
     * What is actually being given up, in messages rather than adjectives.
     *
     * Parked messages are the load-bearing number. A budget of 4 with nothing waiting is an instance that has
     * no more work to do; a budget of 4 with eleven messages parked and the oldest thirty seconds in is an
     * instance that HAS work and has declined to start it — and only the second one is costing throughput.
     */
    taskWaitingLine(t: TaskIntake): string | null {
        if (t.waiting <= 0) return null;
        const oldest = t.oldestWaitSeconds == null
            ? ''
            : ` (oldest waiting ${this.durationLabel(t.oldestWaitSeconds)})`;
        return `${t.waiting} message${t.waiting === 1 ? '' : 's'} already taken from the broker ${t.waiting === 1 ? 'is' : 'are'} parked waiting for a free slot${oldest}.`;
    }

    /**
     * THE SAME QUESTION, CUMULATIVELY — and the only form of it that survives the moment.
     *
     * `waiting` and `oldestWaitSeconds` above are spot readings on a one-second poll, and the gate empties
     * and refills far faster than that: an instance whose budget is genuinely the thing holding it back shows
     * "nothing parked" on most polls, and every trace of a stall between two polls is simply gone. These
     * three remember. `delayed` against `admitted` is how much of this instance's work has ever had to queue
     * for a slot — 300 of 12,000 is a budget that is barely in the way, 300 of 400 is a budget that is the
     * whole story — and the longest observed wait is the worst it ever got.
     *
     * Null until the gate has actually admitted something: "0 messages, 0 delayed" on a freshly started
     * instance is a line that teaches nothing and trains the eye to skip the row.
     */
    taskSinceStartLine(t: TaskIntake): string | null {
        if (!t.admitted || t.admitted <= 0) return null;

        const admitted = `${t.admitted.toLocaleString()} message${t.admitted === 1 ? '' : 's'} worked since this instance started`;
        if (!t.delayed || t.delayed <= 0)
            return `${admitted}, none of which had to wait for a slot — the gate has never held this instance back.`;

        const share = Math.round((t.delayed / t.admitted) * 100);
        const longest = t.longestWaitSeconds > 0
            ? ` The longest any one message waited was ${this.durationLabel(t.longestWaitSeconds)}.`
            : '';
        return `${admitted}; ${t.delayed.toLocaleString()} of them (${share}%) had to wait for a slot first.${longest}`;
    }

    /** Where the slot count came from, so "I want more" has an address. */
    budgetSourceLine(t: TaskIntake): string {
        return t.budgetSource === 'Operator'
            ? `Slot count set explicitly (Consumers:AdaptiveIntake:Budget).`
            : `Slot count derived: ${t.poolSize} connections per database pool, less ${t.reserve} reserved for the admin API, projections, sagas and health checks.`;
    }

    /** The full arithmetic, verbatim from the API — the same sentence it writes to the startup log. */
    budgetTooltip(t: TaskIntake): string {
        return t.adaptiveIntakeEnabled
            ? t.budgetArithmetic
            : `${t.budgetArithmetic} The pool backoff is DISABLED, so the gate holds the derived number and is never lowered under pressure.`;
    }

    /** THE ENTIRE VISIBLE SURFACE OF THE ONLY RUNTIME ADAPTATION LEFT. Present only while it is actually
     *  happening; when it is absent, every number on this page was decided when its endpoint bound and has
     *  not moved since. */
    backoffLine(t: TaskIntake): string | null {
        if (!t.backingOff) return null;
        const since = t.backingOffForSeconds == null
            ? ''
            : ` — the database pool had callers queued ${this.durationLabel(t.backingOffForSeconds)} ago`;
        return `Backing off: gate ${t.limit} of ${t.derived}${since}. Restores in one step after 30 clean `
            + 'seconds. No queue is rebound to apply this, so the per-queue numbers below are unchanged.';
    }

    // ---- the expanded panel: sums, under headings that name their scale -----------------------------
    //
    // Everything below is a CONSUMER-WIDE number. None of it belongs on the row, where every figure is per
    // queue, and none of it is a sum of per-queue ceilings — that quantity ("2,400 worker slots") is the one
    // the platform itself documents as unreachable, and a denominator nobody may reach is not a denominator.

    /**
     * NEVER "X of Y" WHERE X CAN EXCEED Y — the same rule the panel below applies, applied to its heading.
     *
     * `locationCount` is the MERCHANT TABLE's count and `live` is the endpoints this instance has bound, and
     * the two come from different places: an in-memory transport binds a location-scoped consumer's base
     * endpoint with no merchant rows at all, and the heading rendered "This consumer — 1 of 0 queues live".
     * `targetLine` has always guarded this with `locationCount > live`; this did not, so one screen printed a
     * fraction the other refused to. Above the merchant count there is no denominator worth printing, so it
     * says what is live and stops.
     */
    consumerScopeHeading(row: ConsumerRow): string {
        const live = row.intake ? row.intake.endpoints : row.endpointCount;
        if (!row.perLocation) return 'This consumer — its one queue';
        return row.locationCount > live
            ? `This consumer — ${live} of ${row.locationCount} ${row.locationCount === 1 ? 'queue' : 'queues'} live`
            : `This consumer — all ${live} live ${live === 1 ? 'queue' : 'queues'}`;
    }

    /** THE ASK, SHOWN AS A CONSEQUENCE. This is the arithmetic the validator used to refuse with — "50 x 7
     *  endpoints = 350 messages in flight against a task budget of 30" — and it is still worth showing. What
     *  changed is that the sentence ends in what each queue will run rather than in a division the operator
     *  has to perform before they are allowed to save.
     *
     *  NO SUM AND NO DENOMINATOR. Both quantities here are per queue, which is the only scale this consumer
     *  has: what one queue was asked to run, and what one queue will run.
     *
     *  THE DIVISOR IS THE API'S, NOT THIS PAGE'S. It used to be `intake.endpoints` — the endpoints THIS
     *  INSTANCE has bound right now — which is a different number from the one the reconcile loop divides by
     *  and a third one again from what the save banner used. Over seven merchants with one Magento instance
     *  switched off, this line said 100 per queue and the banner beneath it promised 85. */
    consumerAskLine(row: ConsumerRow): string {
        const i = row.intake;
        if (!i) return 'None of this consumer\'s queues is bound on this instance, so nothing is allocated to it.';
        if (row.targetValue == null) return `${i.endpoints} live ${i.endpoints === 1 ? 'queue' : 'queues'}.`;

        const n = (v: number) => v.toLocaleString('en-US');
        const queues = `${n(row.targetQueues)} live ${row.targetQueues === 1 ? 'queue' : 'queues'}`;
        // THE SAME `targetValue` THE ROW AND THE BOX SHOW. It used to come off the intake fold, which only the
        // list route could fill — so a second after the page loaded this line read "Target 0 per queue".
        const perQueueTarget = row.targetShared
            ? Math.max(1, Math.floor(row.targetValue / row.targetQueues))
            : row.targetValue;
        const ask = row.targetShared
            ? `Target ${n(row.targetValue)} shared across ${queues} = ${n(perQueueTarget)} per queue`
            : `Target ${n(row.targetValue)} per queue, on ${queues}`;

        return `${ask}. Every one of them is bound to work ${this.perQueueInForce(row)} at once.`;
    }

    /** ONE QUEUE'S ARITHMETIC, spelled out — target, and the buffer that follows it. Present on every row,
     *  including the healthy ones: a number explained only when something is wrong is a number nobody has
     *  learned to read by the time something is.
     *
     *  Per queue, like everything else in this panel's first block. What the INSTANCE runs is the block below
     *  it, under a heading that says so. */
    queueWhyLine(row: ConsumerRow): string | null {
        const i = row.intake;
        if (!i || !i.why) return null;
        return i.why;
    }

    /** WHAT THE CEILINGS ADD UP TO, AND WHY THAT IS NOT A PROMISE — the instance's answer to an ambitious
     *  target, printed under the instance heading where its scale is named.
     *
     *  This is the sentence that replaces the old per-queue division. The queue really is bound at the target;
     *  the reason six queues of 600 is not 3,600 messages of work is the gate, and that is worth one line on
     *  the same screen as the number that made somebody ask. */
    gateLine(row: ConsumerRow, t: TaskIntake): string {
        const n = (v: number) => v.toLocaleString('en-US');
        const live = row.intake ? row.intake.endpoints : row.endpointCount;
        const bound = row.intake ? row.intake.highestConcurrency : row.concurrentMessageLimit;
        const queues = t.endpoints === 1 ? '1 queue' : `${n(t.endpoints)} queues`;

        const ceilings = bound != null && live > 0
            ? `This consumer's queues are entitled to ${n(live)} x ${n(bound)} = ${n(live * bound)} between `
              + 'them, and every other consumer on this instance has its own entitlement. '
            : '';

        return `${ceilings}What the instance actually runs at once is ${n(t.derived)}, across all ${queues} it `
            + 'hosts, held by one intake gate every message passes through. A ceiling is what a queue MAY run: '
            + 'a large target buys this consumer the whole instance while the other queues are quiet, and '
            + 'nothing at all while they are not. Nothing is refused and nothing is lost — messages this '
            + 'instance does not take stay in RabbitMQ for another instance to work.';
    }

    /** WHAT IS RUNNING, per queue, and the buffer beside it. Both the same scale as each other and as the row
     *  above, so there is no pair here for a reader to reconcile. */
    consumerInForceLine(row: ConsumerRow): string {
        const i = row.intake;
        if (!i) return '';
        const queues = i.endpoints === 1 ? 'its one live queue' : `each of its ${i.endpoints} live queues`;
        return `In force now: ${this.perQueueInForce(row)} at once and ${this.perQueuePrefetch(row)} buffered on `
            + `${queues}. Fixed when the queue bound; it moves only when the queue is rebound.`;
    }

    /** Prefetch, explained where there is room for it — it is the usual reason a queue with a healthy target
     *  still drains one message at a time. */
    prefetchExplainLine(row: ConsumerRow): string {
        if (row.prefetchCount == null) return '';
        const provenance = row.prefetchSource === 'derived'
            ? '20% above the workers'
            : `from ${row.prefetchSource === 'override' ? 'a stored override' : 'an environment setting'}`;
        return `Prefetch ${row.prefetchCount} per queue, ${provenance}. It is the buffer, not the worker `
            + 'count: it exists so a free worker never waits on a broker round trip, and nothing more. Every '
            + 'buffered message is unacked and invisible to every other instance until this one gets to it, '
            + 'which is why it is not a dial.';
    }

    /** ONE MERCHANT'S QUEUE, in the locations dialog. EVERY NUMBER IN THIS MODAL IS PER QUEUE — there is no
     *  instance figure here at all, not even in a tooltip, because a modal that lists merchants is a modal
     *  where a second scale reads as the first one. The instance's own numbers live in the strip and the
     *  panel, under headings that name them.
     *
     *  It used to end in ", bound by database pool", which was true when the derivation divided the instance
     *  across its endpoints and is not true of anything now: this queue is bound at its target. */
    locationIntakeLine(intake: IntakeStatus): string {
        return `${intake.concurrency} at once, ${intake.prefetch} buffered`;
    }

    locationIntakeTooltip(intake: IntakeStatus): string {
        return intake.why
            ? intake.why
            : `This queue was bound to work ${intake.concurrency} at once with a buffer of ${intake.prefetch}. `
              + 'Both are fixed by the transport when the endpoint binds.';
    }


    /** "42s", "3m 20s", "1h 4m" — read at a glance, never a bare count of seconds past a minute. */
    durationLabel(seconds: number): string {
        const total = Math.max(0, Math.round(seconds));
        if (total < 60) return `${total}s`;
        if (total < 3600) {
            const rem = total % 60;
            return rem === 0 ? `${Math.floor(total / 60)}m` : `${Math.floor(total / 60)}m ${rem}s`;
        }
        const mins = Math.floor((total % 3600) / 60);
        return mins === 0 ? `${Math.floor(total / 3600)}h` : `${Math.floor(total / 3600)}h ${mins}m`;
    }

    /**
     * The page-level banner, present only when something is actually held back.
     *
     * Two situations, and the distinction is the point of the whole feature. Some slots given up while the
     * pool recovers is the backoff WORKING, and the right response is to do nothing. The gate at its floor
     * with work parked is the instance OUT OF ROOM, and this banner is the only place that shows
     * during this failure: the handlers await async I/O, so threads, CPU and memory all stay flat while it
     * happens, and the dashboards built on them show a healthy process consuming nothing.
     */
    readonly throttleBanner = computed<{ severity: 'warn' | 'error'; text: string } | null>(() => {
        const t = this.task();
        if (!t || !t.backingOff) return null;

        const forHowLong = t.backingOffForSeconds == null
            ? ''
            : ` (${this.durationLabel(t.backingOffForSeconds)} so far)`;
        const parked = t.waiting > 0
            ? ` ${t.waiting} message${t.waiting === 1 ? '' : 's'} already taken from the broker ${t.waiting === 1 ? 'is' : 'are'} parked waiting for a slot.`
            : ' Nothing is parked waiting for a slot, so no queue is being held up by it yet.';

        // AT THE FLOOR WITH WORK PARKED is the one state that calls for an operator. Anything above it is the
        // backoff doing its job: the pool is short, this instance is taking less of it, and it will have the
        // whole derived number back thirty clean seconds later.
        if (t.limit <= 1 && t.waiting > 0) {
            return {
                severity: 'error',
                text: `This instance is working one message at a time${forHowLong}, because the database `
                    + 'connection pool has callers queued waiting for a connection, and it has work parked that '
                    + 'it has declined to start. There is nothing left to give up: if the queues are not '
                    + 'draining, add another instance. Raising a target here will not help — the pool, not any '
                    + 'setting on this page, is the limit.'
            };
        }

        return {
            severity: 'warn',
            text: `This instance is working ${t.limit} messages at once instead of the ${t.derived} it `
                + `derived${forHowLong}, because the database connection pool had callers queued waiting for a `
                + 'connection. Nothing below has been rebound and no per-queue number has moved; the gate goes '
                + `back to ${t.derived} in one step after thirty seconds with a clear pool.${parked}`
        };
    });

    // ---- controls --------------------------------------------------------------------------------

    isSaving(key: string): boolean {
        return this.saving().has(key);
    }

    openLocations(row: ConsumerRow): void {
        this.locationsNote.set(null);
        this.locationsFor.set(row.consumerKey);
        this.locationsOpen.set(true);
        this.loadLocations(row.consumerKey);
    }

    private loadLocations(consumerKey: string): void {
        this.locationsLoading.set(true);
        this.locationsError.set(null);
        this.api.getConsumerLocations(consumerKey).subscribe({
            next: (d) => {
                this.locations.set(d ? d : []);
                this.locationsLoading.set(false);
            },
            error: (e) => {
                this.locations.set([]);
                this.locationsError.set(this.describe(e));
                this.locationsLoading.set(false);
            }
        });
    }

    sourceSeverity(source: string | null | undefined): 'warn' | 'info' | 'secondary' {
        if (source === 'override') return 'warn';
        if (source === 'configured') return 'info';
        return 'secondary';
    }

    // Consumer-level pause: writes the consumer's own row, which every one of its endpoints falls back to.
    // A location paused individually keeps its own row and stays paused — the row says how many.
    toggleConsumer(row: ConsumerRow, enabled: boolean): void {
        this.save(row.consumerKey, enabled);
    }

    // One location's queue, from inside the dialog. Writes the composed key, which can pause that location
    // on its own but never widen its consumer's pause: a location runs only when the consumer is enabled AND
    // the location is (QueueConcurrency.EnabledFor). Refreshes the dialog as well as the page behind it,
    // since the row's own status is what just changed.
    toggleLocation(location: LocationRow, enabled: boolean): void {
        const consumerKey = this.locationsFor();
        this.setSaving(location.key, true);
        this.locationsError.set(null);
        this.api.setConsumerEnabled(location.key, enabled).subscribe({
            next: () => {
                this.reload();
                if (consumerKey) this.loadLocations(consumerKey);
            },
            error: (e) => {
                this.locationsError.set(this.describe(e));
                this.setSaving(location.key, false);
                if (consumerKey) this.loadLocations(consumerKey); // revert to server truth
            }
        });
    }

    private save(key: string, enabled: boolean): void {
        this.setSaving(key, true);
        this.error.set(null);
        this.api.setConsumerEnabled(key, enabled).subscribe({
            next: () => this.reload(),
            error: (e) => {
                this.error.set(this.describe(e));
                this.setSaving(key, false);
                this.reload(); // revert the switch back to server truth
            }
        });
    }

    // PREFILLED WITH THE TARGET THE ROW IS SHOWING — the same field, so the box and the row can never
    // disagree. They used to: the row printed the effective per-queue number (already clamped) and
    // the box printed the stored override, which is how one screenshot showed a ceiling of 400 above a box
    // reading 800.
    //
    // The value it opened with is remembered as the BASELINE so a box left alone can still be told from one
    // deliberately set to the same number: only a field that differs from its baseline is written, which is
    // what stops a glance from pinning today's default forever. The prefetch override rides along unchanged
    // for the same reason — the form can no longer display it, so an unrelated edit must not clear it.
    startEdit(row: ConsumerRow): void {
        this.capsError.set(null);
        this.capsExplanation.set(null);
        this.draft.set({
            consumerKey: row.consumerKey,
            target: row.targetValue,
            baseTarget: row.targetValue,
            shared: row.targetShared,
            baseShared: row.targetShared,
            prefetch: row.prefetchOverride
        });
        this.editing.set(row.consumerKey);
    }

    cancelEdit(): void {
        this.editing.set(null);
        this.capsError.set(null);
        this.capsExplanation.set(null);
    }

    setDraftTarget(value: number | null): void {
        this.draft.set({ ...this.draft(), target: value == null ? null : value });
    }

    setDraftShared(value: boolean): void {
        this.draft.set({ ...this.draft(), shared: value });
    }

    applyCaps(row: ConsumerRow): void {
        const d = this.draft();
        // Baselines travel with the request; the API decides per field whether anything is written. The
        // prefetch pair is submitted equal so the API's untouched rule leaves any stored value exactly as it
        // is — sending a bare null with no baseline would CLEAR it, which is not what "I edited the target"
        // means.
        this.saveCaps(row, d.prefetch, d.target, d.prefetch, d.baseTarget, d.shared);
    }

    // The explicit clear: absolute nulls, no baselines, so both stored values go regardless of what the form
    // happens to show. This is the only path that can still remove a legacy prefetch override, which is why
    // the field survives on the request at all.
    clearCaps(row: ConsumerRow): void {
        this.saveCaps(row, null, null, null, null, null);
    }

    private saveCaps(
        row: ConsumerRow,
        prefetch: number | null,
        concurrency: number | null,
        prefetchBaseline: number | null,
        concurrencyBaseline: number | null,
        shared: boolean | null
    ): void {
        this.setSaving(row.consumerKey, true);
        this.capsError.set(null);
        this.capsExplanation.set(null);
        // Always the CONSUMER key: the setting covers every per-location endpoint it runs.
        this.api.setConsumerConcurrency(row.consumerKey, prefetch, concurrency, prefetchBaseline, concurrencyBaseline, shared).subscribe({
            next: (saved) => {
                // ACCEPTED, AND HERE IS WHAT IT WILL DO. Set before the reload and deliberately not cleared by
                // it: the row this refers to is the one that just saved, and a banner that vanished on the
                // next list response would be a banner nobody ever finished reading.
                this.capsExplanation.set(saved && saved.explanation ? saved.explanation : null);
                this.editing.set(null);
                this.reload();
                // THE DIALOG HOLDS ITS OWN COPY. It was fetched when it opened, so after a caps change it
                // keeps showing the numbers from before — the consumer row saying 5000/4000 above a list of
                // rows all saying 2000/1800. Refresh it whenever it is open on the consumer just changed.
                if (this.locationsOpen() && this.locationsFor() === row.consumerKey)
                    this.loadLocations(row.consumerKey);
            },
            error: (e) => {
                // Whatever the API refused, its explanation belongs next to the input rather than in a toast
                // that vanishes before it is read. It no longer refuses a starved buffer — the prefetch is
                // floored at the worker count where it binds, so that pair cannot be produced — but a target
                // above the representable ceiling, a non-positive number and a save that races a concurrent
                // edit all still come back here.
                this.capsError.set(this.describe(e));
                this.setSaving(row.consumerKey, false);
            }
        });
    }

    /** The management UI lives one level above the API root the middleware calls: strip the trailing /api. */
    managementUi(apiRoot: string): string {
        return apiRoot.replace(/\/api$/, '');
    }

    private reload(): void {
        this.loading.set(true);
        this.api.getConsumersBroker().subscribe({
            next: (b) => this.broker.set(b),
            error: () => this.broker.set(null)
        });
        this.api.getConsumersOutbox().subscribe({
            next: (ob) => this.outbox.set(ob),
            error: () => this.outbox.set(null)
        });
        this.api.getConsumers().subscribe({
            next: (d) => {
                this.consumers.set(d ? d : []);
                // Every row carries the same task budget; the first one that has it is as good as any. This is
                // what makes the strip correct on the first paint instead of a second later.
                const withTask = (d ? d : []).find((c) => !!c.task);
                if (withTask && withTask.task) this.task.set(withTask.task);
                this.loading.set(false);
                this.saving.set(new Set());
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.loading.set(false);
                this.saving.set(new Set());
            }
        });
    }

    private setSaving(key: string, on: boolean): void {
        const next = new Set(this.saving());
        if (on) next.add(key);
        else next.delete(key);
        this.saving.set(next);
    }

    /** Show what is parked in this queue's error sibling, so Replay is an informed press rather than a
     *  hopeful one. Read-only on the server: the messages are fetched and requeued. */
    viewErrors(ep: { key: string; consumerKey: string; locationId: string | null }): void {
        this.errorsLoading.set(ep.key);
        this.api.peekConsumerErrors(ep.consumerKey, ep.locationId).subscribe({
            next: (r) => {
                this.errorsQueue.set(r.queue);
                this.errorMessages.set(r.messages);
                this.errorsOpen.set(true);
                this.errorsLoading.set(null);
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.errorsLoading.set(null);
            }
        });
    }

    /** REBUILD A MERCHANT'S BROKER SIDE. Onboarding provisions the vhost and binds the queues, but neither
     *  step is allowed to fail the onboarding — so a merchant that saved while the broker was unreachable is
     *  correct in the database and missing from RabbitMQ, and its endpoint sits Failed with a 400 nobody can
     *  act on. This asks again and shows what came back.
     *
     *  The dialog is refreshed afterwards because the whole point is to see the row stop being Failed; the
     *  reconcile pass the server runs is synchronous, so by the time this returns the status is real. */
    repair(ep: { locationId: string }): void {
        this.repairing.set(ep.locationId);
        this.repairResult.update(r => ({ ...r, [ep.locationId]: { error: null } }));

        this.api.provisionLocation(ep.locationId).subscribe({
            next: (result) => {
                this.repairResult.update(r => ({ ...r, [ep.locationId]: { error: result.error } }));
                this.repairing.set(null);
                if (this.locationsFor()) this.loadLocations(this.locationsFor()!);
            },
            error: (e) => {
                this.repairResult.update(r => ({ ...r, [ep.locationId]: { error: this.describe(e) } }));
                this.repairing.set(null);
            }
        });
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — your role lacks the consumers:manage permission.';
        // Validation refusals arrive as { error: "..." } and carry the reason the operator needs.
        if (e?.error?.error) return e.error.error;
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
